import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { Edit2, Archive, Plus } from 'lucide-react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
  // Timestamp, // Timestamp might not be needed directly here
} from 'firebase/firestore';
import { Button, Table } from '../ui'; // Assuming Table and Button accept translated strings/props
import AddEditPriceTierModal from '../AddEditPriceTierModal'; // Already localized
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureUpgradeModal } from '../../contexts/FeatureUpgradeModalContext';
import { db } from '../../lib/firebase';
import { SessionPriceTier } from '../../types/firestoreTypes';

// StatusPill component - translation will happen where it's used
const StatusPill: React.FC<{ isArchived: boolean; label: string }> = ({
  isArchived,
  label,
}) => {
  return (
    <span
      className={`inline-flex px-2 py-1 rounded-full text-sm font-medium ${
        isArchived
          ? 'bg-[#FEF3F2] text-[#B42318]' // Archived styles
          : 'bg-[#ECFDF3] text-[#027A48]' // Active styles
      }`}
    >
      {label} {/* Display the translated label */}
    </span>
  );
};

const PriceTiersSettingsTab: React.FC = () => {
  const { t } = useTranslation(); // Get the t function
  const { user, therapist } = useAuth();
  const { openUpgradeModal } = useFeatureUpgradeModal();
  const [priceTiers, setPriceTiers] = useState<SessionPriceTier[]>([]);
  const [loading, setLoading] = useState(true);
  // Store translation key for error messages
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isPriceTierModalOpen, setIsPriceTierModalOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<SessionPriceTier | null>(null);
  const [currentPage, setCurrentPage] = useState(1); // Assuming Table handles pagination display
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    setErrorKey(null); // Clear previous errors

    const q = query(
      collection(db, 'sessionPriceTiers'),
      where('therapistId', '==', user.uid),
      orderBy('tierName')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const tiers: SessionPriceTier[] = [];
        snapshot.forEach((doc) => {
          tiers.push({
            id: doc.id,
            ...(doc.data() as Omit<SessionPriceTier, 'id'>),
          });
        });
        setPriceTiers(tiers);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching price tiers:', err);
        setErrorKey('settings.priceTiers.errors.loadFailed'); // Use key
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const filteredTiers = showArchived
    ? priceTiers
    : priceTiers.filter((tier) => !tier.isArchived);

  const handleEditTier = (tier: SessionPriceTier) => {
    setEditingTier(tier);
    setIsPriceTierModalOpen(true);
  };

  const handleArchiveTier = async (tier: SessionPriceTier) => {
    // Check if this is the primary tier
    if (therapist?.primaryPriceTierId === tier.id) {
      setErrorKey('settings.priceTiers.errors.cannotArchivePrimary');
      return;
    }

    // Use translated confirmation message
    if (!window.confirm(t('settings.priceTiers.confirmations.archive'))) {
      return;
    }
    setErrorKey(null); // Clear previous errors

    try {
      const tierRef = doc(db, 'sessionPriceTiers', tier.id);
      await updateDoc(tierRef, {
        isArchived: true,
        updatedAt: serverTimestamp(),
      });
      // Optionally add a success toast/message key here
    } catch (err) {
      console.error('Error archiving price tier:', err);
      setErrorKey('settings.priceTiers.errors.archiveFailed'); // Use key
    }
  };

  const handleAddNewTier = () => {
    // --- Feature Limit Check ---
    const priceTierLimit = therapist?.packageFeaturesSnapshot?.PRICE_TIER_LIMIT;
    const currentActivePriceTierCount = priceTiers.filter(tier => !tier.isArchived).length;

    // Check if priceTierLimit is a number and if the limit has been reached.
    // If priceTierLimit is not defined or not a number, or if limit is 0 (treat as potentially unlimited/misconfigured), allow creation.
    if (typeof priceTierLimit === 'number' && priceTierLimit > 0 && currentActivePriceTierCount >= priceTierLimit) {
      openUpgradeModal({
        featureNameKey: 'featureUpgradeModal.featureNames.priceTierLimit',
        customMessageKey: 'featureUpgradeModal.messages.priceTierLimitReached',
      });
      return; // Stop before opening the modal
    }
    // --- End: Feature Limit Check ---

    setEditingTier(null);
    setIsPriceTierModalOpen(true);
  };

  const handleSetPrimary = async (tier: SessionPriceTier) => {
    if (!user) return;

    // Use translated confirmation message
    if (!window.confirm(t('settings.priceTiers.confirmations.setPrimary'))) {
      return;
    }
    setErrorKey(null); // Clear previous errors

    try {
      const therapistRef = doc(db, 'therapists', user.uid);
      await updateDoc(therapistRef, {
        primaryPriceTierId: tier.id,
        updatedAt: serverTimestamp(),
      });
      // Optionally add a success toast/message key here
    } catch (err) {
      console.error('Error setting primary tier:', err);
      setErrorKey('settings.priceTiers.errors.setPrimaryFailed'); // Use key
    }
  };

  const handleSaveTier = async (
    // Type correctly based on AddEditPriceTierModal's onSave prop
    formData: Pick<
      SessionPriceTier,
      'tierName' | 'price' | 'currency' | 'durationMinutes'
    >
  ) => {
    if (!user) return;
    setErrorKey(null); // Clear previous errors

    try {
      if (editingTier) {
        // Update existing tier
        const tierRef = doc(db, 'sessionPriceTiers', editingTier.id);
        await updateDoc(tierRef, {
          ...formData, // Spread the validated form data
          updatedAt: serverTimestamp(),
        });
      } else {
        // Add new tier
        const newTierRef = await addDoc(collection(db, 'sessionPriceTiers'), {
          ...formData, // Spread the validated form data
          therapistId: user.uid,
          isArchived: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // If this is the first tier, automatically set it as primary
        if (priceTiers.filter(tier => !tier.isArchived).length === 0) {
          const therapistRef = doc(db, 'therapists', user.uid);
          await updateDoc(therapistRef, {
            primaryPriceTierId: newTierRef.id,
            updatedAt: serverTimestamp(),
          });
        }
      }

      setIsPriceTierModalOpen(false);
      setEditingTier(null);
      // Optionally add a success toast/message key here
    } catch (err) {
      console.error('Error saving price tier:', err);
      setErrorKey('settings.priceTiers.errors.saveFailed'); // Use key
    }
  };

  // Define columns with translated headers
  const columns = [
    { key: 'tierName', header: t('settings.priceTiers.table.headers.name') },
    {
      key: 'price',
      header: t('settings.priceTiers.table.headers.price'),
      render: (tier: SessionPriceTier) =>
        `${tier.currency} ${
          tier.currency === 'HUF'
            ? Math.round(tier.price)
            : tier.price.toFixed(2)
        }`, // Formatting logic remains
    },
    {
      key: 'durationMinutes',
      header: t('settings.priceTiers.table.headers.duration'),
      // Translate unit in render function
      render: (tier: SessionPriceTier) =>
        `${tier.durationMinutes} ${t(
          'settings.priceTiers.table.durationUnit'
        )}`,
    },
    {
      key: 'currency',
      header: t('settings.priceTiers.table.headers.currency'),
    },
    {
      key: 'status',
      header: t('settings.priceTiers.table.headers.status'),
      // Translate status label for the pill here
      render: (tier: SessionPriceTier) => {
        const isPrimary = therapist?.primaryPriceTierId === tier.id;
        return (
          <div className="flex gap-2">
            <StatusPill
              isArchived={tier.isArchived}
              label={t(
                tier.isArchived
                  ? 'settings.priceTiers.status.archived'
                  : 'settings.priceTiers.status.active'
              )}
            />
            {isPrimary && (
              <span className="inline-flex px-2 py-1 rounded-full text-sm font-medium bg-[#F0F9FF] text-[#0369A1] border border-[#BAE6FD]">
                {t('settings.priceTiers.status.primary')}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: t('settings.priceTiers.table.headers.actions'),
      render: (tier: SessionPriceTier) => {
        const isPrimary = therapist?.primaryPriceTierId === tier.id;
        return (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Edit2 className="w-4 h-4" />}
              onClick={() => handleEditTier(tier)}
              aria-label={t('settings.priceTiers.buttons.edit')} // Accessibility
            >
              {t('settings.priceTiers.buttons.edit')}
            </Button>
            {!tier.isArchived && !isPrimary && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleSetPrimary(tier)}
                aria-label={t('settings.priceTiers.buttons.setPrimary')} // Accessibility
              >
                {t('settings.priceTiers.buttons.setPrimary')}
              </Button>
            )}
            {!tier.isArchived && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Archive className="w-4 h-4" />}
                onClick={() => handleArchiveTier(tier)}
                disabled={isPrimary}
                aria-label={
                  isPrimary
                    ? t('settings.priceTiers.buttons.cannotArchivePrimary')
                    : t('settings.priceTiers.buttons.archive')
                } // Accessibility
                title={
                  isPrimary
                    ? t('settings.priceTiers.tooltips.cannotArchivePrimary')
                    : undefined
                } // Tooltip
              >
                {t('settings.priceTiers.buttons.archive')}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  // Display translated error message if errorKey is set
  if (errorKey) {
    return (
      <div className="p-4 bg-red-50 rounded-lg text-red-700">{t(errorKey)}</div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-[#101828]">
            {t('settings.priceTiers.heading')} {/* Translate heading */}
          </h2>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 text-[#7F56D9] border-[#D0D5DD] rounded focus:ring-[#7F56D9]"
            />
            <span className="text-sm text-[#344054]">
              {t('settings.priceTiers.showArchivedLabel')}{' '}
              {/* Translate label */}
            </span>
          </label>
        </div>
        <Button
          variant="primary"
          onClick={handleAddNewTier}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          {t('settings.priceTiers.buttons.addNew')}{' '}
          {/* Translate button text */}
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-[#EAECF0]">
        <Table
          columns={columns} // Columns now have translated headers
          data={filteredTiers}
          isLoading={loading}
          pagination={
            filteredTiers.length > 5 // Only show pagination if needed (example logic)
              ? {
                  currentPage,
                  // Calculate total pages based on filtered data and items per page (e.g., 5)
                  totalPages: Math.ceil(filteredTiers.length / 5),
                  onPageChange: setCurrentPage,
                  // Assuming Table component handles its own internal text like "Page x of y"
                }
              : undefined // No pagination if fewer items
          }
          // Assuming Table component handles "No data" message internally or via a prop
          // noDataMessage={t('table.noData')}
        />
      </div>

      {/* Modal is already localized and receives translated data if needed */}
      <AddEditPriceTierModal
        isOpen={isPriceTierModalOpen}
        onClose={() => {
          setIsPriceTierModalOpen(false);
          setEditingTier(null);
        }}
        onSave={handleSaveTier}
        initialData={editingTier}
      />
    </div>
  );
};

export default PriceTiersSettingsTab;
